import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ButtonIcon } from '@resideo/blueprint-oneportal';
import { Scheduler, type SchedulerEvent } from '@resideo/blueprint-react';
import { chevronLeftIcon } from '@/assets/icons';
import styles from './NotificationBulkSchedulePage.module.css';

interface SchedulerDate {
    toString(): string;
}

interface TimeRangeSelectedArgs {
    start: SchedulerDate;
    end: SchedulerDate;
}

interface EventMovedArgs {
    e: { data: { id: string | number } };
    newStart: SchedulerDate;
    newEnd: SchedulerDate;
}

interface NotificationScheduleNavigationState {
    activeSection?: string;
    notificationSchedule?: SchedulerEvent[];
    notificationSelections?: { email: string[]; push: string[] };
    notificationsEnabled?: boolean;
}

interface PeriodPopoverState {
    visible: boolean;
    eventId: string | number | null;
    startTime: string;
    endTime: string;
    allDay: boolean;
    date: string;
    top: number;
    left: number;
    error: string;
}

const CLOSED_POPOVER: PeriodPopoverState = {
    visible: false,
    eventId: null,
    startTime: '08:00',
    endTime: '17:00',
    allDay: false,
    date: '',
    top: 0,
    left: 0,
    error: '',
};

const POPOVER_WIDTH = 480;
const POPOVER_HEIGHT = 340;
const POPOVER_OFFSET = 14;

function getWeekStart(): string {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
}

function getPopoverPosition(x: number, y: number): { top: number; left: number } {
    const left = x + POPOVER_WIDTH + POPOVER_OFFSET > window.innerWidth - 8
        ? x - POPOVER_WIDTH - POPOVER_OFFSET
        : x + POPOVER_OFFSET;
    const top = y + POPOVER_HEIGHT + POPOVER_OFFSET > window.innerHeight - 8
        ? y - POPOVER_HEIGHT - POPOVER_OFFSET
        : y + POPOVER_OFFSET;

    return {
        top: Math.max(8, top),
        left: Math.max(8, Math.min(left, window.innerWidth - POPOVER_WIDTH - 8)),
    };
}

function hasTimeOverlap(
    events: SchedulerEvent[],
    date: string,
    startTime: string,
    endTime: string,
    excludedEventId: string | number | null,
): boolean {
    return events
        .filter(event => String(event.start).slice(0, 10) === date && event.id !== excludedEventId)
        .some(event => {
            const eventStart = String(event.start).slice(11, 16);
            const eventEnd = String(event.end).slice(11, 16);
            return startTime < eventEnd && eventStart < endTime;
        });
}

const NotificationBulkSchedulePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const navigationState = location.state as NotificationScheduleNavigationState | null;
    const [events, setEvents] = useState<SchedulerEvent[]>(navigationState?.notificationSchedule ?? []);
    const [popover, setPopover] = useState<PeriodPopoverState>(CLOSED_POPOVER);
    const popoverRef = useRef<HTMLDivElement>(null);
    const lastMousePosition = useRef({ x: 0, y: 0 });
    const pendingEventRef = useRef<SchedulerEvent | null>(null);
    const weekStart = getWeekStart();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const title = t('bulkEditSettings.notifications.schedule.addTitle');

    useEffect(() => {
        const trackMousePosition = (event: MouseEvent) => {
            lastMousePosition.current = { x: event.clientX, y: event.clientY };
        };
        document.addEventListener('mousemove', trackMousePosition, { passive: true });
        document.addEventListener('mouseup', trackMousePosition, { passive: true });
        return () => {
            document.removeEventListener('mousemove', trackMousePosition);
            document.removeEventListener('mouseup', trackMousePosition);
        };
    }, []);

    useEffect(() => {
        if (!popover.visible) return undefined;
        const closePopoverOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                setPopover(CLOSED_POPOVER);
                pendingEventRef.current = null;
            }
        };
        document.addEventListener('mousedown', closePopoverOutside);
        return () => document.removeEventListener('mousedown', closePopoverOutside);
    }, [popover.visible]);

    const handleTimeRangeSelected = ({ start, end }: TimeRangeSelectedArgs) => {
        const startDate = start.toString();
        const endDate = end.toString();
        const position = getPopoverPosition(lastMousePosition.current.x, lastMousePosition.current.y);
        setPopover({
            visible: true,
            eventId: null,
            startTime: startDate.slice(11, 16),
            endTime: endDate.slice(11, 16),
            allDay: false,
            date: startDate.slice(0, 10),
            ...position,
            error: '',
        });
    };

    const openEditPopover = (event: SchedulerEvent) => {
        const startDate = String(event.start);
        const endDate = String(event.end);
        const position = getPopoverPosition(lastMousePosition.current.x, lastMousePosition.current.y);
        pendingEventRef.current = event;
        setPopover({
            visible: true,
            eventId: event.id,
            startTime: startDate.slice(11, 16),
            endTime: endDate.slice(11, 16),
            allDay: startDate.slice(11, 16) === '00:00' && endDate.slice(11, 16) === '23:59',
            date: startDate.slice(0, 10),
            ...position,
            error: '',
        });
    };

    const handlePopoverSave = () => {
        const { eventId, date, startTime, endTime, allDay } = popover;
        if (!startTime || !endTime) {
            setPopover(previous => ({
                ...previous,
                error: t('bulkEditSettings.notifications.schedule.timeRequired'),
            }));
            return;
        }
        if (startTime >= endTime) {
            setPopover(previous => ({
                ...previous,
                error: t('bulkEditSettings.notifications.schedule.endAfterStart'),
            }));
            return;
        }
        if (hasTimeOverlap(events, date, startTime, endTime, eventId)) {
            setPopover(previous => ({
                ...previous,
                error: t('bulkEditSettings.notifications.schedule.overlapError'),
            }));
            return;
        }

        const nextEvent: SchedulerEvent = {
            id: eventId ?? `notification-${Date.now()}`,
            text: t('bulkEditSettings.notifications.schedule.timeRange', { start: startTime, end: endTime }),
            start: `${date}T${startTime}:00`,
            end: `${date}T${endTime}${allDay ? ':59' : ':00'}`,
        };
        setEvents(previous => eventId === null
            ? [...previous, nextEvent]
            : previous.map(event => event.id === eventId ? nextEvent : event)
        );
        setPopover(CLOSED_POPOVER);
        pendingEventRef.current = null;
    };

    const handlePopoverDelete = () => {
        if (popover.eventId !== null) {
            setEvents(previous => previous.filter(event => event.id !== popover.eventId));
        }
        setPopover(CLOSED_POPOVER);
        pendingEventRef.current = null;
    };

    const handleEventMove = ({ e, newStart, newEnd }: EventMovedArgs) => {
        setEvents(prev => prev.map(event => event.id === e.data.id
            ? {
                ...event,
                start: newStart.toString(),
                end: newEnd.toString(),
                text: t('bulkEditSettings.notifications.schedule.timeRange', {
                    start: newStart.toString().slice(11, 16),
                    end: newEnd.toString().slice(11, 16),
                }),
            }
            : event
        ));
    };

    const handleEventResize = (event: SchedulerEvent) => {
        setEvents(prev => prev.map(current => current.id === event.id
            ? {
                ...current,
                ...event,
                start: String(event.start),
                end: String(event.end),
                text: t('bulkEditSettings.notifications.schedule.timeRange', {
                    start: String(event.start).slice(11, 16),
                    end: String(event.end).slice(11, 16),
                }),
            }
            : current
        ));
    };

    const returnToSettings = (schedule: SchedulerEvent[]) => {
        const cameras = searchParams.get('cameras') ?? '';
        navigate(`/camera/bulk-edit?cameras=${encodeURIComponent(cameras)}`, {
            state: {
                ...navigationState,
                activeSection: 'notifications',
                notificationSchedule: schedule,
            },
        });
    };

    return (
        <main className={styles.page}>
            <header className={styles.header}>
                <ButtonIcon
                    size="small"
                    iconType="icon"
                    aria-label={t('common.back')}
                    onClick={() => navigate(-1)}
                >
                    <img src={chevronLeftIcon} className={styles.backIcon} alt="" aria-hidden="true" />
                </ButtonIcon>
                <h1 className={styles.title}>{title}</h1>
                <div className={styles.actions}>
                    <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        disabled={events.length === 0}
                        onClick={() => returnToSettings(events)}
                    >
                        {t('bulkEditSettings.notifications.schedule.save')}
                    </Button>
                </div>
            </header>

            <section className={styles.content} aria-label={title}>
                <div className={styles.calendar}>
                    <Scheduler
                        startDate={weekStart}
                        weekStarts={1}
                        events={events}
                        businessBeginsHour={0}
                        businessEndsHour={24}
                        allowEventOverlap={false}
                        eventMoveHandling="Update"
                        eventResizeHandling="Update"
                        timeRangeSelectedHandling="Enabled"
                        timeZone={timeZone}
                        onTimeRangeSelected={handleTimeRangeSelected}
                        onEventContextMenu={(event: SchedulerEvent) => { pendingEventRef.current = event; }}
                        onEditPeriod={() => {
                            if (pendingEventRef.current) openEditPopover(pendingEventRef.current);
                        }}
                        onContextMenuClose={() => undefined}
                        onEventMove={handleEventMove}
                        onEventResize={handleEventResize}
                        eventStyles={() => ({
                            backgroundColor: '#CCDBFC',
                            borderColor: '#82A9E7',
                            borderWidth: '1.5px',
                            borderRadius: '8px',
                        })}
                    />
                </div>
                {popover.visible && (
                    <div
                        ref={popoverRef}
                        className={styles.popoverAnchor}
                        style={{ top: popover.top, left: popover.left }}
                    >
                        <div className={styles.periodPopover} role="dialog" aria-label={t('bulkEditSettings.notifications.schedule.editPeriod')}>
                            <div className={styles.popoverHeader}>
                                <span className={styles.popoverTitle}>{t('bulkEditSettings.notifications.schedule.editPeriod')}</span>
                                <Button
                                    type="button"
                                    variant="text"
                                    className={styles.deletePeriodButton}
                                    aria-label={t('bulkEditSettings.notifications.schedule.deletePeriod')}
                                    disabled={popover.eventId === null}
                                    onClick={handlePopoverDelete}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 9h8l1-9H3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </Button>
                            </div>
                            <div className={styles.popoverTimeRow}>
                                <label className={styles.popoverTimeField}>
                                    <span>{t('bulkEditSettings.notifications.schedule.startTime')}</span>
                                    <input
                                        aria-label={t('bulkEditSettings.notifications.schedule.startTime')}
                                        type="time"
                                        value={popover.startTime}
                                        disabled={popover.allDay}
                                        onChange={event => setPopover(previous => ({ ...previous, startTime: event.target.value, error: '' }))}
                                    />
                                </label>
                                <label className={styles.popoverTimeField}>
                                    <span>{t('bulkEditSettings.notifications.schedule.endTime')}</span>
                                    <input
                                        aria-label={t('bulkEditSettings.notifications.schedule.endTime')}
                                        type="time"
                                        value={popover.endTime}
                                        disabled={popover.allDay}
                                        onChange={event => setPopover(previous => ({ ...previous, endTime: event.target.value, error: '' }))}
                                    />
                                </label>
                            </div>
                            <label className={styles.allDayControl}>
                                <input
                                    type="checkbox"
                                    checked={popover.allDay}
                                    onChange={event => setPopover(previous => ({
                                        ...previous,
                                        allDay: event.target.checked,
                                        startTime: event.target.checked ? '00:00' : '08:00',
                                        endTime: event.target.checked ? '23:59' : '17:00',
                                        error: '',
                                    }))}
                                />
                                {t('bulkEditSettings.notifications.schedule.allDay')}
                            </label>
                            {popover.error && <p className={styles.popoverError} role="alert">{popover.error}</p>}
                            <div className={styles.popoverActions}>
                                <Button type="button" variant="secondary" onClick={() => setPopover(CLOSED_POPOVER)}>
                                    {t('common.cancel')}
                                </Button>
                                <Button type="button" variant="primary" onClick={handlePopoverSave}>
                                    {t('common.add')}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </main>
    );
};

export default NotificationBulkSchedulePage;